import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { SumsubService } from '@/core/services/sumsubService';
import SumsubWebSdk from '@sumsub/websdk-react';
import Loading from '@/core/components/globals/loading';
import { toast } from 'react-toastify';
import { TravelRuleStatus } from '@/store/auth/enum';

interface TravelRuleModalProps {
  onClose: () => void;
  isFullModal?: boolean;
}

const TravelRuleModal: React.FC<TravelRuleModalProps> = ({ onClose, isFullModal }) => {
  const { t, i18n } = useTranslation();
  const [showWebSdk, setShowWebSdk] = useState(false);
  const [accessToken, setAccessToken] = useState('');
  const [loading, setLoading] = useState<boolean>(true);
  const [isDeposit, setIsDeposit] = useState<boolean>(true);
  const [statusCheckTimer, setStatusCheckTimer] = useState<NodeJS.Timeout | null>(null);

  // Fetch the travel rule status
  useQuery({
    queryKey: ['travelRuleStatus'],
    queryFn: SumsubService.getTravelRuleStatus,
    onSuccess: (data) => {
      setIsDeposit(data.is_deposit);
    },
    onError: (error) => {
      console.error('Error fetching travel rule status:', error);
    },
  });

  const accessTokenExpirationHandler = async () => {
    const type = isDeposit ? 'deposit' : 'withdrawal';
    const token = (await SumsubService.getTravelRuleToken(type)).token;
    setAccessToken(token);
    setLoading(false);
  }

  const handleNext = async () => {
    setShowWebSdk(true);
    await accessTokenExpirationHandler();
  };

  const timerGetTravelRuleStatus = () => {
    // Clear any existing timer
    if (statusCheckTimer) {
      clearTimeout(statusCheckTimer);
    }

    const timer = setTimeout(() => {
      SumsubService.getTravelRuleStatus().then((data) => {
        if (data.status === TravelRuleStatus.COMPLETED || data.status === TravelRuleStatus.FAILED) {
          // If completed or failed, stop checking
          console.log('Travel rule status check completed with status:', data.status);
          setStatusCheckTimer(null);
        } else if (data.status === TravelRuleStatus.SENT) {
          // If still in SENT status, check again after 30 seconds
          timerGetTravelRuleStatus();
        }
      }).catch((error) => {
        console.error('Error fetching travel rule status:', error);
        setStatusCheckTimer(null);
      });
    }, 30000); // Check every 30 seconds

    setStatusCheckTimer(timer);
    return timer;
  };

  // Cleanup timer on component unmount
  useEffect(() => {
    return () => {
      if (statusCheckTimer) {
        clearTimeout(statusCheckTimer);
      }
    };
  }, [statusCheckTimer]);

  const messageHandler = (message: unknown) => {
    if (message == 'idCheck.onStepCompleted') {
      // Start checking the travel rule status every 30 seconds
      timerGetTravelRuleStatus();

      onClose();
      toast.success(t('common.success'));
    }
  };

  const errorHandler = (error: unknown) => {
    console.error('Error from Sumsub WebSDK:', error);
  };

  const config = {
    lang: (i18n?.language || "en")
  };

  const options = {};

  if (isFullModal) return (
    <div className="general-modal" style={{ width: "100%", maxWidth: 750, maxHeight: "100%" }}>
      {!showWebSdk ? (
        <div className="dv-form__guide">
          <div className="general-modal__header">
            {t('common.complementary_info')}
          </div>
          <div className="general-modal__content">
            <p>
              {t('travel-rule.description_1')} {' '}
              {isDeposit ? t('travel-rule.deposit') : t('travel-rule.withdraw')} {' '}
              {t('travel-rule.description_2')}
            </p>
            <p>
              {t('travel-rule.enter_info')}
            </p>
          </div>
          <div className="general-modal__footer">
            <button
              className="cta-button cta-medium cta-blue"
              onClick={handleNext}
            >
              <span>{t('common.next')}</span>
            </button>
          </div>
        </div>
      ) : (
        <div className="custom-scrollbar" style={{ maxHeight: "600px" }}>
          { loading ? (
            <Loading />
          ) : (
            <SumsubWebSdk
              accessToken={accessToken}
              expirationHandler={accessTokenExpirationHandler}
              config={config}
              options={options}
              onMessage={messageHandler}
              onError={errorHandler}
            />
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
      {!showWebSdk ? (
        <div className="dv-form__guide">
          <div className="general-modal__content">
            <p>
              {t('travel-rule.description_1')} {' '}
              {isDeposit ? t('travel-rule.deposit') : t('travel-rule.withdraw')} {' '}
              {t('travel-rule.description_2')}
            </p>
            <p>
              {t('travel-rule.enter_info')}
            </p>
          </div>
          <div className="general-modal__footer">
            <button
              className="cta-button cta-medium cta-blue"
              onClick={handleNext}
            >
              <span>{t('common.next')}</span>
            </button>
          </div>
        </div>
      ) : (
        <div className="custom-scrollbar" style={{ maxHeight: "600px" }}>
          { loading ? (
            <Loading />
          ) : (
            <SumsubWebSdk
              accessToken={accessToken}
              expirationHandler={accessTokenExpirationHandler}
              config={config}
              options={options}
              onMessage={messageHandler}
              onError={errorHandler}
            />
          )}
        </div>
      )}
    </>
  );
};

export default TravelRuleModal;
